



























import { useVModel } from '@vueuse/core';
import {
  computed,
  defineComponent,
  PropType,
  ref,
  onMounted,
  watch
} from '@vue/composition-api';

import { createNamespacedHelpers, useStore } from 'vuex-composition-helpers';

import { IRootState } from '@/store';
import {
  IWorkflowFormActions,
  IWorkflowFormGetters,
  IWorkflowFormMutations,
  IWorkflowFormState
} from '@/store/workflow-form';
import { setErrorFromAxiosError } from '@/utils/helpers';

import { IUser } from '@/models';
import { ISystemError } from '@/models/error/error.types';

import { VAutocomplete } from 'vuetify/lib/components';

export default defineComponent({
  name: 'WorkflowFormStateAvailableAssigneePicker',

  props: {
    submissionId: { type: Number, required: true },
    stateId: { type: Number, required: true },

    value: [Array, Number, Object] as PropType<
      IUser | number | (IUser | number)[]
    >,

    errorMessages: {
      type: [String, Array] as PropType<string | string[]>
    }
  },

  setup(props, { emit }) {
    const assigneeVModel = useVModel(props);

    const store = useStore<IRootState>();

    const isLoading = ref(false);

    const error = ref<ISystemError | null>(null);

    const errorMessageList = computed(() => {
      let errorList: string[] = [];

      if (error.value) {
        errorList.push(error.value.message);
      }
      if (props.errorMessages) {
        if (Array.isArray(props.errorMessages)) {
          errorList.push(...props.errorMessages);
        } else {
          errorList.push(props.errorMessages);
        }
      }

      return errorList;
    });

    const { useActions, useGetters } = createNamespacedHelpers<
      IWorkflowFormState,
      IWorkflowFormGetters,
      IWorkflowFormActions,
      IWorkflowFormMutations
    >(store, 'workflowForm');

    const { fetchWorkflowFormSubmissionsAvailableAssignees } = useActions([
      'fetchWorkflowFormSubmissionsAvailableAssignees'
    ]);

    const {
      getWorkflowFormSubmissionsAvailableAssignees: getAssignees
    } = useGetters(['getWorkflowFormSubmissionsAvailableAssignees']);

    function loadAvailableAssignees() {
      error.value = null;
      isLoading.value = true;

      fetchWorkflowFormSubmissionsAvailableAssignees({
        submissionId: props.submissionId,
        stateId: props.stateId
      })
        .then(() => {
          if (getAssignees.value.length === 0) {
            emit('assignee-list-empty');
          }
        })
        .catch(err => {
          error.value = setErrorFromAxiosError(err);
        })
        .finally(() => {
          isLoading.value = false;
        });
    }

    watch(
      () => props.stateId,
      () => {
        loadAvailableAssignees();
      }
    );

    onMounted(() => {
      loadAvailableAssignees();
    });

    // /** @public */
    const autocompleteRef = ref<
      InstanceType<typeof VAutocomplete> & { reset: () => void }
    >();

    /** @public */
    function reset() {
      if (autocompleteRef.value) autocompleteRef.value.reset();
    }

    return {
      reset,
      autocompleteRef,
      assigneeVModel,
      getAssignees,
      isLoading,
      errorMessageList
    };
  }
});
